import { gql, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import {
  UpdateArticleMutation,
  UpdateArticleMutationVariables,
  UpsertArticleMutation,
  UpsertArticleMutationVariables,
} from '../../__generated__/queries-web';
import { GET_ARTICLE_QUERY } from '../queries/articles.web.graphql';

const UPSERT_ARTICLE = gql`
  mutation UpsertArticle(
    $create: ArticleCreationInput!
    $update: ArticleUpdateInput!
    $where: ArticleUniqueFilterInput!
  ) {
    upsertArticle(create: $create, update: $update, where: $where) {
      id
      title
      status
      body
      createdAt
      publishedAt
      unpublishAt
      editedAt
      brandKey
      format
      medias
      pushCount
      canonical
      live {
        postCount
      }
      categories(first: 2, orderBy: order_ASC) {
        order
        category {
          id
          title
        }
      }
      channels(first: 1) {
        order
        category {
          id
          title
        }
      }
      creator {
        ... on OneUser {
          name
        }
      }
      urls(first: 1, orderBy: order_ASC) {
        origin
        path
      }
      qualifiers(first: 100) {
        order
        qualifier {
          id
          title
        }
      }
      lockedAt
      lockedBy {
        id
        name
        email
        avatarUrl
      }
      secondaryTitle
      partnerTitle
      discoverTitle
      lead
      slug
      live {
        postCount
        endedAt
      }
      tags(first: 20, orderBy: order_ASC) {
        order
        tag {
          id
          relationId
          title
          type
        }
      }
      authors(first: 10) {
        order
        author {
          id
          name
          user {
            id
            name
            avatarUrl
          }
        }
      }
      source {
        id
        title
        status
      }
      events(first: 100) {
        order
        category {
          id
          title
        }
      }
      qualifiers(first: 100) {
        order
        qualifier {
          id
          title
        }
      }
      guides(first: 1) {
        order
        category {
          id
          title
        }
      }
      machineTags(first: 100) {
        order
        machineTag {
          id
          tag
        }
      }
      histories(first: 100, orderBy: createdAt_DESC) {
        id
        createdAt
        user {
          ... on OneUser {
            id
            name
            avatarUrl
          }
        }
        article {
          id
        }
      }
      platformId
    }
  }
`;

const UPDATE_ARTICLE = gql`
  mutation UpdateArticle(
    $where: ArticleUniqueFilterInput!
    $data: ArticleUpdateInput!
  ) {
    updateArticle(where: $where, data: $data) {
      id
      title
      status
      body
      createdAt
      publishedAt
      editedAt
      brandKey
      format
      medias
      pushCount
      canonical
      live {
        postCount
      }
      categories(first: 2, orderBy: order_ASC) {
        order
        category {
          id
          title
        }
      }
      channels(first: 1) {
        order
        category {
          id
          title
        }
      }
      creator {
        name
      }
      urls(first: 1, orderBy: order_ASC) {
        origin
        path
      }
      qualifiers(first: 100) {
        order
        qualifier {
          id
          title
        }
      }
      lockedAt
      lockedBy {
        id
        name
        email
        avatarUrl
      }
      secondaryTitle
      partnerTitle
      discoverTitle
      lead
      slug
      live {
        postCount
        endedAt
      }
      tags(first: 20, orderBy: order_ASC) {
        order
        tag {
          id
          relationId
          title
          type
        }
      }
      authors(first: 10) {
        order
        author {
          id
          name
          user {
            id
            name
            avatarUrl
          }
        }
      }
      source {
        id
        title
        status
      }
      events(first: 100) {
        order
        category {
          id
          title
        }
      }
      qualifiers(first: 100) {
        order
        qualifier {
          id
          title
        }
      }
      guides(first: 1) {
        order
        category {
          id
          title
        }
      }
      machineTags(first: 100) {
        order
        machineTag {
          id
          tag
        }
      }
      histories(first: 100, orderBy: createdAt_DESC) {
        id
        createdAt
        user {
          ... on OneUser {
            id
            name
            avatarUrl
          }
        }
        article {
          id
        }
      }
      platformId
      bibliographies(first: 100) {
        title
        url
      }
    }
  }
`;

export const useArticleUpsert = () =>
  useMutation<UpsertArticleMutation, UpsertArticleMutationVariables>(
    UPSERT_ARTICLE,
    {
      refetchQueries: [GET_ARTICLE_QUERY],
    },
  );

export const useArticleUpdater = () =>
  useMutation<UpdateArticleMutation, UpdateArticleMutationVariables>(
    UPDATE_ARTICLE,
    {
      refetchQueries: [GET_ARTICLE_QUERY],
    },
  );

export const useUpdateArticleLockerId = () => {
  const [updateArticle] = useMutation<
    UpdateArticleMutation,
    UpdateArticleMutationVariables
  >(UPDATE_ARTICLE);

  return useCallback(
    (id: string, lockerId: string | null, noCacheUpdate?: boolean) =>
      updateArticle({
        variables: {
          where: { id },
          data: { lockerId },
        },
        fetchPolicy: noCacheUpdate ? 'no-cache' : undefined,
      }),
    [updateArticle],
  );
};
